import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { IconButtonComponent } from 'src/app/ui-kit/icon-button/icon-button.component';
import { HelpContentComponent } from './help-content.component';

/** Preferred edge the popover floats toward, relative to its trigger. */
export type HelpPopoverPlacement = 'top' | 'bottom' | 'left' | 'right';

/**
 * Floating help popover. Renders a small icon-button trigger that, on click,
 * opens a CDK Overlay floating beside the button with brief help content.
 *
 * Positioning uses a flexible connected strategy with `withPush`, so on small
 * screens the panel flips and clamps to stay fully on-screen rather than
 * falling back to a sheet. Closes on backdrop click or Escape.
 *
 * @example
 * <app-help-popover title="What's a baseline?" icon="question-mark">
 *   <p>A baseline is the starting measurement…</p>
 * </app-help-popover>
 *
 * @feature shared
 */
@Component({
  selector: 'app-help-popover',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconButtonComponent, HelpContentComponent],
  host: { class: 'inline-flex' },
  template: `
    @if (customTrigger()) {
      <span
        #trigger
        role="button"
        tabindex="0"
        class="inline-flex cursor-pointer"
        aria-haspopup="dialog"
        [attr.aria-label]="ariaLabel()"
        [attr.aria-expanded]="isOpen()"
        (click)="toggle()"
        (keydown.enter)="toggle()"
        (keydown.space)="$event.preventDefault(); toggle()"
      >
        <ng-content select="[helpTrigger]" />
      </span>
    } @else {
      <app-icon-button
        #trigger
        [icon]="icon()"
        [ariaLabel]="ariaLabel()"
        color="info"
        size="xxs"
        aria-haspopup="dialog"
        [attr.aria-expanded]="isOpen()"
        (click)="toggle()"
      />
    }

    <ng-template #panel>
      <div
        class="w-[min(20rem,90vw)] max-w-[90vw] rounded-xl border border-outline bg-surface p-4 shadow-[var(--theme-shadow)]"
        role="dialog"
        [attr.aria-label]="title() || ariaLabel()"
      >
        <app-help-content [title]="title()" [subtitle]="subtitle()" [videoUrl]="videoUrl()" [active]="isOpen()">
          <ng-content />
        </app-help-content>
      </div>
    </ng-template>
  `,
})
export class HelpPopoverComponent implements OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  @ViewChild('trigger', { read: ElementRef }) private trigger!: ElementRef<HTMLElement>;
  @ViewChild('panel') private panel!: TemplateRef<unknown>;

  /** Optional heading shown at the top of the popover. */
  readonly title = input<string>();

  /** Optional secondary line beneath the title. */
  readonly subtitle = input<string>();

  /** Optional Vimeo reference rendered as a player above the body. */
  readonly videoUrl = input<string>();

  /** Trigger icon filename from `assets/icons/`. @default 'help-circle-outline' */
  readonly icon = input<string>('help-circle-outline');

  /** Accessible label for the icon-only trigger. @default 'Help' */
  readonly ariaLabel = input<string>('Help');

  /** Preferred placement; flips to fit when there isn't room. @default 'bottom' */
  readonly placement = input<HelpPopoverPlacement>('bottom');

  /**
   * When true, the default info icon-button is replaced by content projected
   * into the `[helpTrigger]` slot (e.g. a clickable text label). The projected
   * element is wrapped in a keyboard-accessible button role that toggles the
   * popover. @default false
   */
  readonly customTrigger = input(false);

  /** Whether the popover is currently open. */
  readonly isOpen = signal(false);

  private overlayRef?: OverlayRef;

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.isOpen()) return;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.trigger)
      .withPush(true)
      .withFlexibleDimensions(false)
      .withViewportMargin(8)
      .withPositions(this.buildPositions());

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    this.overlayRef.attach(new TemplatePortal(this.panel, this.viewContainerRef));
    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.overlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    });

    this.isOpen.set(true);
  }

  close(): void {
    if (!this.overlayRef) return;
    this.overlayRef.dispose();
    this.overlayRef = undefined;
    this.isOpen.set(false);
    this.trigger?.nativeElement?.focus();
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  /** Builds the connected-position list with the preferred placement first and the rest as fallbacks. */
  private buildPositions(): ConnectedPosition[] {
    const byPlacement: Record<HelpPopoverPlacement, ConnectedPosition> = {
      bottom: { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
      top: { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
      right: { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
      left: { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
    };
    const order: HelpPopoverPlacement[] = [this.placement(), 'bottom', 'top', 'right', 'left'];
    const seen = new Set<HelpPopoverPlacement>();
    return order.filter(placement => (seen.has(placement) ? false : seen.add(placement))).map(p => byPlacement[p]);
  }
}
